import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { WA_DEFAULT, useSectionNav } from "./shared";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Upgrades", href: "#upgrades" },
  { label: "Security", href: "#security" },
  { label: "More", href: "#extras" },
  { label: "Process", href: "#process" },
  { label: "Location", href: "#location" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const goToSection = useSectionNav();

  const go = (href) => {
    setOpen(false);
    goToSection(href);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-void/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo-button"
          onClick={() => go("#top")}
          className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime rounded-md"
        >
          <img
            src="/assets/logo.png"
            alt="AFZ Tech Solutions logo"
            className="h-11 w-11 object-contain drop-shadow-[0_0_12px_rgba(170,255,0,0.45)]"
          />
          <span className="font-display text-xl font-bold uppercase tracking-wide text-white leading-none">
            AFZ <span className="text-brand-lime">Tech Solutions</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-brand-lime transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime rounded-sm"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-button"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 bg-brand-lime text-void font-display font-bold uppercase tracking-wide text-sm px-5 py-2.5 rounded-full hover:bg-brand-limedark transition-colors duration-300 shadow-[0_0_18px_rgba(170,255,0,0.35)]"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp Us
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2 focus:outline-none"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-navy/95 border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-mono text-sm uppercase tracking-[0.2em] text-slate-300 hover:text-brand-lime"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="nav-mobile-whatsapp-button"
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-lime text-void font-display font-bold uppercase px-5 py-3 rounded-full"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
